/**
 * Hook to manage backend JWT token storage
 * Retrieves token from NextAuth session and stores it for API calls
 */

'use client'

import { useEffect } from 'react'
import { useSession } from 'next-auth/react'
import { setApiToken } from '@/libs/api'

export const useBackendToken = () => {
  const { data: session, status } = useSession()

  useEffect(() => {
    // When session is available, check if it has a backend token
    // and store it in both localStorage and sessionStorage for redundancy
    if (status === 'loading') {
      // Debug logging while loading
      if (process.env.NODE_ENV === 'development') {
        console.log('[BackendTokenManager] Session loading...')
      }
      return
    }

    if (status === 'authenticated' && session && (session as any).backend_access_token) {
      const token = (session as any).backend_access_token

      // Store in both localStorage and sessionStorage for redundancy
      setApiToken(token)

      if (globalThis.window) {
        sessionStorage.setItem('backend_jwt_token', token)

        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[BackendTokenManager] Token stored:', {
            hasToken: !!token,
            tokenLength: token?.length,
            storedInLocalStorage: !!localStorage.getItem('backend_jwt_token'),
            storedInSessionStorage: !!sessionStorage.getItem('backend_jwt_token')
          })
        }
      }
    } else if (status === 'unauthenticated' || !session) {
      // Clear token if session is not available
      setApiToken(null)

      if (globalThis.window) {
        sessionStorage.removeItem('backend_jwt_token')

        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[BackendTokenManager] Token cleared - no session')
        }
      }
    }
  }, [session, status])
}

